import React, { useState, useEffect } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const HashGeneratorPage = () => {
  const [input, setInput] = useState('');
  const [hashes, setHashes] = useState({
    md5: '',
    sha1: '',
    sha256: '',
    sha384: '',
    sha512: ''
  });
  const [isHashing, setIsHashing] = useState(false);
  const [copiedHash, setCopiedHash] = useState(null);
  const [inputType, setInputType] = useState('text'); // 'text' or 'file'
  const [fileName, setFileName] = useState('');

  // Hash algorithms info
  const hashAlgorithms = [
    { key: 'md5', name: 'MD5', bits: 128, description: 'Fast but not cryptographically secure. Used for checksums.' },
    { key: 'sha1', name: 'SHA-1', bits: 160, description: 'Deprecated for security. Still used for integrity checks.' },
    { key: 'sha256', name: 'SHA-256', bits: 256, description: 'Part of SHA-2 family. Widely used and secure.' },
    { key: 'sha384', name: 'SHA-384', bits: 384, description: 'Truncated version of SHA-512. High security.' },
    { key: 'sha512', name: 'SHA-512', bits: 512, description: 'Strongest SHA-2 variant. Maximum security.' }
  ];

  // MD5 implementation (since Web Crypto API doesn't support it)
  const md5 = (string) => {
    function rotateLeft(value, shift) {
      return (value << shift) | (value >>> (32 - shift));
    }

    function addUnsigned(x, y) {
      const x8 = x & 0x80000000;
      const y8 = y & 0x80000000;
      const x4 = x & 0x40000000;
      const y4 = y & 0x40000000;
      const result = (x & 0x3FFFFFFF) + (y & 0x3FFFFFFF);
      if (x4 & y4) return result ^ 0x80000000 ^ x8 ^ y8;
      if (x4 | y4) {
        if (result & 0x40000000) return result ^ 0xC0000000 ^ x8 ^ y8;
        else return result ^ 0x40000000 ^ x8 ^ y8;
      }
      return result ^ x8 ^ y8;
    }

    function F(x, y, z) { return (x & y) | ((~x) & z); }
    function G(x, y, z) { return (x & z) | (y & (~z)); }
    function H(x, y, z) { return x ^ y ^ z; }
    function I(x, y, z) { return y ^ (x | (~z)); }

    function FF(a, b, c, d, x, s, ac) {
      a = addUnsigned(a, addUnsigned(addUnsigned(F(b, c, d), x), ac));
      return addUnsigned(rotateLeft(a, s), b);
    }
    function GG(a, b, c, d, x, s, ac) {
      a = addUnsigned(a, addUnsigned(addUnsigned(G(b, c, d), x), ac));
      return addUnsigned(rotateLeft(a, s), b);
    }
    function HH(a, b, c, d, x, s, ac) {
      a = addUnsigned(a, addUnsigned(addUnsigned(H(b, c, d), x), ac));
      return addUnsigned(rotateLeft(a, s), b);
    }
    function II(a, b, c, d, x, s, ac) {
      a = addUnsigned(a, addUnsigned(addUnsigned(I(b, c, d), x), ac));
      return addUnsigned(rotateLeft(a, s), b);
    }

    function convertToWordArray(string) {
      let messageLength = string.length;
      let numberOfWords = (((messageLength + 8) - ((messageLength + 8) % 64)) / 64 + 1) * 16;
      let wordArray = new Array(numberOfWords - 1);
      let wordCount;
      let bytePosition = 0;
      let byteCount = 0;
      while (byteCount < messageLength) {
        wordCount = (byteCount - (byteCount % 4)) / 4;
        bytePosition = (byteCount % 4) * 8;
        wordArray[wordCount] = (wordArray[wordCount] | (string.charCodeAt(byteCount) << bytePosition));
        byteCount++;
      }
      wordCount = (byteCount - (byteCount % 4)) / 4;
      bytePosition = (byteCount % 4) * 8;
      wordArray[wordCount] = wordArray[wordCount] | (0x80 << bytePosition);
      wordArray[numberOfWords - 2] = messageLength << 3;
      wordArray[numberOfWords - 1] = messageLength >>> 29;
      return wordArray;
    }

    function wordToHex(value) {
      let hex = '', temp = '', byte, count;
      for (count = 0; count <= 3; count++) {
        byte = (value >>> (count * 8)) & 255;
        temp = '0' + byte.toString(16);
        hex = hex + temp.substr(temp.length - 2, 2);
      }
      return hex;
    }

    let x = convertToWordArray(string);
    let a = 0x67452301, b = 0xEFCDAB89, c = 0x98BADCFE, d = 0x10325476;

    const S11 = 7, S12 = 12, S13 = 17, S14 = 22;
    const S21 = 5, S22 = 9, S23 = 14, S24 = 20;
    const S31 = 4, S32 = 11, S33 = 16, S34 = 23;
    const S41 = 6, S42 = 10, S43 = 15, S44 = 21;

    for (let k = 0; k < x.length; k += 16) {
      let AA = a, BB = b, CC = c, DD = d;

      a = FF(a, b, c, d, x[k + 0], S11, 0xD76AA478);
      d = FF(d, a, b, c, x[k + 1], S12, 0xE8C7B756);
      c = FF(c, d, a, b, x[k + 2], S13, 0x242070DB);
      b = FF(b, c, d, a, x[k + 3], S14, 0xC1BDCEEE);
      a = FF(a, b, c, d, x[k + 4], S11, 0xF57C0FAF);
      d = FF(d, a, b, c, x[k + 5], S12, 0x4787C62A);
      c = FF(c, d, a, b, x[k + 6], S13, 0xA8304613);
      b = FF(b, c, d, a, x[k + 7], S14, 0xFD469501);
      a = FF(a, b, c, d, x[k + 8], S11, 0x698098D8);
      d = FF(d, a, b, c, x[k + 9], S12, 0x8B44F7AF);
      c = FF(c, d, a, b, x[k + 10], S13, 0xFFFF5BB1);
      b = FF(b, c, d, a, x[k + 11], S14, 0x895CD7BE);
      a = FF(a, b, c, d, x[k + 12], S11, 0x6B901122);
      d = FF(d, a, b, c, x[k + 13], S12, 0xFD987193);
      c = FF(c, d, a, b, x[k + 14], S13, 0xA679438E);
      b = FF(b, c, d, a, x[k + 15], S14, 0x49B40821);

      a = GG(a, b, c, d, x[k + 1], S21, 0xF61E2562);
      d = GG(d, a, b, c, x[k + 6], S22, 0xC040B340);
      c = GG(c, d, a, b, x[k + 11], S23, 0x265E5A51);
      b = GG(b, c, d, a, x[k + 0], S24, 0xE9B6C7AA);
      a = GG(a, b, c, d, x[k + 5], S21, 0xD62F105D);
      d = GG(d, a, b, c, x[k + 10], S22, 0x2441453);
      c = GG(c, d, a, b, x[k + 15], S23, 0xD8A1E681);
      b = GG(b, c, d, a, x[k + 4], S24, 0xE7D3FBC8);
      a = GG(a, b, c, d, x[k + 9], S21, 0x21E1CDE6);
      d = GG(d, a, b, c, x[k + 14], S22, 0xC33707D6);
      c = GG(c, d, a, b, x[k + 3], S23, 0xF4D50D87);
      b = GG(b, c, d, a, x[k + 8], S24, 0x455A14ED);
      a = GG(a, b, c, d, x[k + 13], S21, 0xA9E3E905);
      d = GG(d, a, b, c, x[k + 2], S22, 0xFCEFA3F8);
      c = GG(c, d, a, b, x[k + 7], S23, 0x676F02D9);
      b = GG(b, c, d, a, x[k + 12], S24, 0x8D2A4C8A);

      a = HH(a, b, c, d, x[k + 5], S31, 0xFFFA3942);
      d = HH(d, a, b, c, x[k + 8], S32, 0x8771F681);
      c = HH(c, d, a, b, x[k + 11], S33, 0x6D9D6122);
      b = HH(b, c, d, a, x[k + 14], S34, 0xFDE5380C);
      a = HH(a, b, c, d, x[k + 1], S31, 0xA4BEEA44);
      d = HH(d, a, b, c, x[k + 4], S32, 0x4BDECFA9);
      c = HH(c, d, a, b, x[k + 7], S33, 0xF6BB4B60);
      b = HH(b, c, d, a, x[k + 10], S34, 0xBEBFBC70);
      a = HH(a, b, c, d, x[k + 13], S31, 0x289B7EC6);
      d = HH(d, a, b, c, x[k + 0], S32, 0xEAA127FA);
      c = HH(c, d, a, b, x[k + 3], S33, 0xD4EF3085);
      b = HH(b, c, d, a, x[k + 6], S34, 0x4881D05);
      a = HH(a, b, c, d, x[k + 9], S31, 0xD9D4D039);
      d = HH(d, a, b, c, x[k + 12], S32, 0xE6DB99E5);
      c = HH(c, d, a, b, x[k + 15], S33, 0x1FA27CF8);
      b = HH(b, c, d, a, x[k + 2], S34, 0xC4AC5665);

      a = II(a, b, c, d, x[k + 0], S41, 0xF4292244);
      d = II(d, a, b, c, x[k + 7], S42, 0x432AFF97);
      c = II(c, d, a, b, x[k + 14], S43, 0xAB9423A7);
      b = II(b, c, d, a, x[k + 5], S44, 0xFC93A039);
      a = II(a, b, c, d, x[k + 12], S41, 0x655B59C3);
      d = II(d, a, b, c, x[k + 3], S42, 0x8F0CCC92);
      c = II(c, d, a, b, x[k + 10], S43, 0xFFEFF47D);
      b = II(b, c, d, a, x[k + 1], S44, 0x85845DD1);
      a = II(a, b, c, d, x[k + 8], S41, 0x6FA87E4F);
      d = II(d, a, b, c, x[k + 15], S42, 0xFE2CE6E0);
      c = II(c, d, a, b, x[k + 6], S43, 0xA3014314);
      b = II(b, c, d, a, x[k + 13], S44, 0x4E0811A1);
      a = II(a, b, c, d, x[k + 4], S41, 0xF7537E82);
      d = II(d, a, b, c, x[k + 11], S42, 0xBD3AF235);
      c = II(c, d, a, b, x[k + 2], S43, 0x2AD7D2BB);
      b = II(b, c, d, a, x[k + 9], S44, 0xEB86D391);

      a = addUnsigned(a, AA);
      b = addUnsigned(b, BB);
      c = addUnsigned(c, CC);
      d = addUnsigned(d, DD);
    }

    return (wordToHex(a) + wordToHex(b) + wordToHex(c) + wordToHex(d)).toLowerCase();
  };

  // Generate hash using Web Crypto API
  const generateHash = async (algorithm, data) => {
    const encoder = new TextEncoder();
    const dataBuffer = typeof data === 'string' ? encoder.encode(data) : data;
    const hashBuffer = await crypto.subtle.digest(algorithm, dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Generate all hashes
  const generateAllHashes = async (data) => {
    if (!data || (typeof data === 'string' && !data.trim())) {
      setHashes({ md5: '', sha1: '', sha256: '', sha384: '', sha512: '' });
      return;
    }

    setIsHashing(true);
    try {
      const dataForMd5 = typeof data === 'string' ? data : await arrayBufferToString(data);
      
      const [sha1Hash, sha256Hash, sha384Hash, sha512Hash] = await Promise.all([
        generateHash('SHA-1', data),
        generateHash('SHA-256', data),
        generateHash('SHA-384', data),
        generateHash('SHA-512', data)
      ]);

      setHashes({
        md5: md5(dataForMd5),
        sha1: sha1Hash,
        sha256: sha256Hash,
        sha384: sha384Hash,
        sha512: sha512Hash
      });
    } catch (error) {
      console.error('Error generating hashes:', error);
    } finally {
      setIsHashing(false);
    }
  };

  const arrayBufferToString = async (buffer) => {
    const decoder = new TextDecoder('utf-8', { fatal: false });
    return decoder.decode(buffer);
  };

  // Generate hashes when input changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputType === 'text') {
        generateAllHashes(input);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, inputType]);

  // Handle file input
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFileName('');
      setHashes({ md5: '', sha1: '', sha256: '', sha384: '', sha512: '' });
      return;
    }

    setFileName(file.name);
    setIsHashing(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      await generateAllHashes(arrayBuffer);
    } catch (error) {
      console.error('Error reading file:', error);
    }
  };

  // Copy hash to clipboard
  const copyToClipboard = async (hash, key) => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopiedHash(key);
      setTimeout(() => setCopiedHash(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Clear all
  const clearAll = () => {
    setInput('');
    setFileName('');
    setHashes({ md5: '', sha1: '', sha256: '', sha384: '', sha512: '' });
  };

  // Sample texts
  const sampleTexts = [
    { label: 'Hello World', value: 'Hello World' },
    { label: 'Password123', value: 'Password123' },
    { label: 'test@email.com', value: 'test@email.com' },
    { label: 'JSON Object', value: '{"name":"John","age":30}' }
  ];

  return (
    <div>
      <SEO 
        title="Hash Generator - MD5, SHA-1, SHA-256, SHA-512 Online" 
        description="Free online hash generator. Generate MD5, SHA-1, SHA-256, SHA-384, SHA-512 hashes from text or files. Secure, fast, and works offline in your browser."
        keywords="hash generator, md5 generator, sha256 generator, sha512 hash, checksum calculator, hash online, file hash, crypto hash"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Hash Generator</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Generate MD5, SHA-1, SHA-256, SHA-384, and SHA-512 hashes from text or files. All processing happens locally in your browser.
        </p>
        

        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
          {/* Input Section */}
          <div className="input-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Input</h2>
            
            {/* Input Type Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => { setInputType('text'); setFileName(''); }}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: inputType === 'text' ? '#2196f3' : '#e0e0e0',
                    color: inputType === 'text' ? 'white' : '#333',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  📝 Text Input
                </button>
                <button
                  onClick={() => { setInputType('file'); setInput(''); }}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: inputType === 'file' ? '#2196f3' : '#e0e0e0',
                    color: inputType === 'file' ? 'white' : '#333',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  📁 File Input
                </button>
              </div>
            </div>

            {inputType === 'text' ? (
              <>
                {/* Text Input */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Enter text to hash:
                  </label>
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Enter any text to generate hashes..."
                    style={{
                      width: '100%',
                      height: '200px',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {input.length} characters
                  </div>
                </div>

                {/* Sample Texts */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Quick Examples:
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {sampleTexts.map((sample, index) => (
                      <button
                        key={index}
                        onClick={() => setInput(sample.value)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '16px',
                          backgroundColor: 'white',
                          color: '#333',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        {sample.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              /* File Input */
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Select a file:
                </label>
                <div
                  style={{
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer'
                  }}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <input
                    type="file"
                    id="fileInput"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  {fileName ? (
                    <div>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#333' }}>{fileName}</div>
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>Click to change file</div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '48px', marginBottom: '10px', opacity: 0.3 }}>📁</div>
                      <div style={{ fontSize: '16px', color: '#666' }}>Click to select a file</div>
                      <div style={{ fontSize: '14px', color: '#999', marginTop: '5px' }}>or drag and drop</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={clearAll}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🗑️ Clear All
            </button>
          </div>

          {/* Output Section */}
          <div className="output-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Generated Hashes</h2>
            
            {isHashing ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                <div>Generating hashes...</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {hashAlgorithms.map((algo) => (
                  <div
                    key={algo.key}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '15px',
                      backgroundColor: hashes[algo.key] ? '#fff' : '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div>
                        <span style={{ fontWeight: '600', fontSize: '14px' }}>{algo.name}</span>
                        <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>{algo.bits} bits</span>
                      </div>
                      {hashes[algo.key] && (
                        <button
                          onClick={() => copyToClipboard(hashes[algo.key], algo.key)}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: copiedHash === algo.key ? '#4caf50' : 'white',
                            color: copiedHash === algo.key ? 'white' : '#333',
                            cursor: 'pointer',
                            fontSize: '12px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {copiedHash === algo.key ? '✓ Copied' : '📋 Copy'}
                        </button>
                      )}
                    </div>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        wordBreak: 'break-all',
                        backgroundColor: '#f5f5f5',
                        padding: '10px',
                        borderRadius: '4px',
                        color: hashes[algo.key] ? '#333' : '#999',
                        minHeight: '20px'
                      }}
                    >
                      {hashes[algo.key] || 'Enter text or select a file to generate hash'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                      {algo.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About Hash Functions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>What is a Hash?</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                A hash function is a mathematical algorithm that converts data of any size into a fixed-size string of characters.
                The output (hash) is unique to the input data - even a small change produces a completely different hash.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Common Use Cases</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Hashes are used for password storage, file integrity verification, digital signatures, checksums, 
                data deduplication, and blockchain technology.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Security Notes</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                MD5 and SHA-1 are considered cryptographically broken and should not be used for security purposes.
                For secure applications, use SHA-256 or SHA-512.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default HashGeneratorPage;

